import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Factory,
  Hammer,
  Layers3,
  Mail,
  MapPin,
  Phone,
  Ruler,
  ShieldCheck,
  Sparkles,
  Wrench
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type NavItem = {
  label: string;
  href: string;
};

export type TextBlock = {
  eyebrow?: string;
  title: string;
  copy: string;
};

export type Feature = {
  title: string;
  copy: string;
  accent?: "gold" | "blue" | "sand";
  icon?: LucideIcon;
};

export type Capability = {
  label: string;
  summary: string;
};

export const company = {
  name: "Metalworks & Design Group",
  descriptor:
    "Custom metal fabrication, shop detailing, and field-ready delivery. Fully Licensed & Insured",
  phone: "+1 239-241-0386",
  phoneHref: "tel:+12392410386",
  email: "estimating@metalworksdesigngroup.com",
  address: "15050 Elderberry Lane Suite 6, Fort Myers, FL, 33907",
  hours: "Mon-Fri, 8:00 AM-5:00 PM",
  licenseHeadline: "Fully Licensed & Insured:",
  licenseNumbers: "CGC1537790 | CCC1336820"
};

export const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Products", href: "/products" },
  { label: "Projects", href: "/projects" },
  { label: "About Us", href: "/about" },
  { label: "Contact Us", href: "/contact" }
];

export const capabilities: Capability[] = [
  {
    label: "Drawings & Engineering",
    summary:
      "Drawing review, fabrication planning, and engineering coordination that help move project requirements into shop-ready decisions."
  },
  {
    label: "Aluminum",
    summary:
      "Precision-fabricated aluminum components and assemblies designed for durability, clean finishes, and reliable field performance."
  },
  {
    label: "Carbon Steel",
    summary:
      "Durable carbon steel components and assemblies fabricated for strength, reliability, and long-term performance."
  },
  {
    label: "Laser Cutting",
    summary:
      "Accurate, high-quality cut profiles that support efficient fabrication, precise fit-up, and consistent project outcomes."
  },
  {
    label: "Forming",
    summary:
      "Expert brake forming and bending for custom metal components, engineered to maintain accuracy, fit, and performance throughout fabrication and installation."
  },
  {
    label: "Welding",
    summary:
      "High-quality welded assemblies fabricated for accurate fit, structural integrity, clean finishes, and long-term durability in commercial and industrial applications."
  },
  {
    label: "Powder Coating",
    summary:
      "Professional powder-coated finishes designed for durability, corrosion resistance, and lasting visual appeal."
  },
  {
    label: "Assembly",
    summary:
      "Coordinated fit-up, hardware checks, and final preparation that help fabricated components arrive ready for delivery or installation."
  },
  {
    label: "Coordination & Management",
    summary:
      "Project coordination support for schedules, materials, finishing requirements, delivery needs, and communication between shop and field teams."
  },
  {
    label: "Installation",
    summary:
      "Installation-minded planning and field support for metalwork that needs accurate handoff, sequencing, and reliable final placement."
  }
];

export const homeFeatures: Feature[] = [
  {
    title: "Fabrication across critical metals",
    copy: "Material-led planning for stainless, aluminum, carbon steel, and specialty project requirements.",
    accent: "gold",
    icon: Factory
  },
  {
    title: "Cutting and forming in one workflow",
    copy: "Precision cut parts move through forming, welding, and prep with fewer handoff gaps.",
    accent: "blue",
    icon: Ruler
  },
  {
    title: "Finished for field and showroom conditions",
    copy: "Assemblies can be cleaned, fitted, powder coated, and prepared for installation or delivery.",
    accent: "sand",
    icon: Sparkles
  }
];

export const proofPoints: Feature[] = [
  {
    title: "Responsive quoting",
    copy: "Early review clarifies drawings, material constraints, and the next fabrication step.",
    icon: ClipboardCheck
  },
  {
    title: "Fabrication depth",
    copy: "The shop supports one-off custom work, repeatable runs, and mixed-scope production.",
    icon: Wrench
  },
  {
    title: "Detail-led finishing",
    copy: "Finish choices are aligned with use conditions, visibility, and installation requirements.",
    icon: ShieldCheck
  }
];

export const services: Feature[] = [
  {
    title: "Preconstruction and design assist",
    copy: "Scope review, material recommendations, fabrication feasibility, and budget-informed planning.",
    icon: Building2
  },
  {
    title: "Shop drawings and engineering coordination",
    copy: "Detailing support that translates field requirements into shop-ready decisions.",
    icon: ClipboardCheck
  },
  {
    title: "Custom metal fabrication",
    copy: "Cutting, forming, welding, fitting, and assembly for custom and repeatable metal work.",
    icon: Hammer
  },
  {
    title: "Finishing and assembly preparation",
    copy: "Surface prep, powder coating coordination, hardware checks, and final assembly planning.",
    icon: Sparkles
  },
  {
    title: "Site logistics and install support",
    copy: "Delivery coordination, installation sequencing, and fabrication support for field conditions.",
    icon: MapPin
  }
];

export const products = [
  "Bollards",
  "Column Guard Protector",
  "Pipe Cover Protector",
  "Elevator Hoist Beam",
  "Elevator Separator Beam",
  "Elevator Sump Pit Grate",
  "Elevator Pit Ladder",
  "Elevator Pit Angle",
  "Fence",
  "Gate",
  "Railing",
  "Guardrail",
  "Handrail",
  "Louver",
  "Decorative Screen",
  "Trellis",
  "Canopy",
  "Sunshade",
  "Bahama Shutter",
  "Spiral Stair",
  "Roof Caged Ladder",
  "Fixed Ladder",
  "Dumpster Gate"
];

export const projectTypes: Feature[] = [
  {
    title: "Commercial metalwork",
    copy: "Generic project support for commercial scopes that need detailing, fabrication, finishing, delivery, or installation coordination.",
    accent: "gold",
    icon: Building2
  },
  {
    title: "Industrial fabrication support",
    copy: "Generic project support for industrial components, facility upgrades, production-adjacent metalwork, and practical field needs.",
    accent: "blue",
    icon: Factory
  },
  {
    title: "Architectural details",
    copy: "Generic project support for visible metal details, feature elements, trims, frames, and assemblies that require careful finishing.",
    accent: "sand",
    icon: Layers3
  },
  {
    title: "Custom fabrication projects",
    copy: "Generic project support for unique fabrication challenges, replacement parts, specialty assemblies, and evolving scopes.",
    accent: "gold",
    icon: Wrench
  }
];

export const aboutValues: Feature[] = [
  {
    title: "Practical by default",
    copy: "Recommendations stay grounded in material behavior, shop reality, and the way the work will be used.",
    icon: CheckCircle2
  },
  {
    title: "Clear before cutting",
    copy: "The team pushes decisions forward early, reducing ambiguity before fabrication starts.",
    icon: ClipboardCheck
  },
  {
    title: "Built for handoff",
    copy: "Finished pieces are prepared with delivery, install, and long-term use in mind.",
    icon: ArrowRight
  }
];

export const contactDetails = [
  { label: "Call", value: company.phone, icon: Phone },
  { label: "Email", value: company.email, icon: Mail },
  { label: "Visit", value: company.address, icon: MapPin },
  { label: "Hours", value: company.hours, icon: CheckCircle2 }
];

export const footerHighlights = [
  "Design assist",
  "Laser cutting",
  "Forming",
  "Welding",
  "Powder coating",
  "Install support"
];

export const footerProcess = [
  "Share drawings or scope",
  "Review materials and finish",
  "Confirm fabrication path",
  "Prepare for delivery"
];
